/**
 * 安全配置服务
 * Security Configuration Service
 * 
 * 此服务负责在运行时安全地加载配置信息，避免敏感信息被硬编码到构建产物中
 * This service is responsible for securely loading configuration at runtime,
 * preventing sensitive information from being hardcoded into build artifacts
 */

interface AppConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  apiBaseUrl: string;
  appName: string;
  appVersion: string;
  isDebugMode: boolean;
  enableAnalytics: boolean;
  enableSentry: boolean;
}

class ConfigurationService {
  private config: AppConfig | null = null;
  private isInitialized = false;

  /**
   * 初始化配置服务
   * Initialize configuration service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // 检查是否在开发环境或本地预览
      const isDev = import.meta.env.DEV;
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

      if (isDev || (isLocalhost && import.meta.env.VITE_SUPABASE_URL)) {
        // 开发环境或本地预览：使用 Vite 环境变量
        console.log('🔧 [ConfigService] Using development/local configuration');
        this.config = {
          supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
          supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
          apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787',
          appName: import.meta.env.VITE_APP_NAME || 'Eastern Fate Master',
          appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
          isDebugMode: import.meta.env.VITE_APP_DEBUG === 'true',
          enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
          enableSentry: import.meta.env.VITE_ENABLE_SENTRY === 'true',
        };
      } else {
        // 生产环境：尝试从后端 API 加载配置，失败时回退到构建时环境变量
        console.log('🔧 [ConfigService] Using production configuration');
        try {
          console.log('🔧 [ConfigService] Attempting to load from secure backend API...');
          await this.loadProductionConfig();
        } catch (error) {
          console.warn('⚠️ [ConfigService] API config failed, falling back to build-time environment variables:', (error as any).message);
          this.loadFallbackConfig();
        }
      }

      // 验证必需的配置
      this.validateConfig();
      this.isInitialized = true;

    } catch (error) {
      console.error('Configuration initialization failed:', error);
      throw new Error('Failed to initialize application configuration');
    }
  }

  /**
   * 回退配置加载 - 使用构建时环境变量
   * Fallback configuration loading using build-time environment variables
   */
  private loadFallbackConfig(): void {
    console.log('🔧 [ConfigService] Using fallback build-time configuration');
    
    this.config = {
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL || '',
      supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL || '',
      appName: import.meta.env.VITE_APP_NAME || 'Eastern Fate Master',
      appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0',
      isDebugMode: import.meta.env.VITE_APP_DEBUG === 'true',
      enableAnalytics: import.meta.env.VITE_ENABLE_ANALYTICS === 'true',
      enableSentry: import.meta.env.VITE_ENABLE_SENTRY === 'true',
    };
    
    console.log('✅ [ConfigService] Fallback configuration loaded');
  }

  /**
   * 加载生产环境配置
   * Load production configuration from secure backend API
   */
  private async loadProductionConfig(): Promise<void> {
    try {
      // 🔧 [SECURITY REFACTOR] 使用相对路径，通过统一网关访问
      const configUrl = '/api/config';

      console.log('🔧 [ConfigService] Loading production config from:', configUrl);

      // 从后端API安全获取配置
      const response = await fetch(configUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        // 不发送认证信息，这是公开的配置端点
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Config API returned ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();

      // 检查API响应格式
      if (!responseData.success || !responseData.data) {
        throw new Error('Invalid config API response format');
      }

      const config = responseData.data;

      console.log('✅ [ConfigService] Production config loaded successfully');
      console.log('🔍 [ConfigService] Config timestamp:', config.CONFIG_TIMESTAMP);

      // For security, the backend does not return any secrets (e.g., ANON_KEY). Use build-time env for those.
      const supabaseUrl = config.SUPABASE_URL || import.meta.env.VITE_SUPABASE_URL || '';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      this.config = {
        supabaseUrl,
        supabaseAnonKey,
        apiBaseUrl: '', // 🔧 [SECURITY REFACTOR] 不再需要API基础URL，使用相对路径
        appName: config.APP_NAME || import.meta.env.VITE_APP_NAME || 'Eastern Fate Master',
        appVersion: config.APP_VERSION || import.meta.env.VITE_APP_VERSION || '1.0.0',
        isDebugMode: (config.APP_DEBUG || import.meta.env.VITE_APP_DEBUG) === 'true',
        enableAnalytics: (config.ENABLE_ANALYTICS || import.meta.env.VITE_ENABLE_ANALYTICS) === 'true',
        enableSentry: (config.ENABLE_SENTRY || import.meta.env.VITE_ENABLE_SENTRY) === 'true',
      };

      return;

    } catch (error) {
      console.error('❌ [ConfigService] Failed to load production config:', error);
      throw new Error(`Configuration loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * 验证配置的完整性和安全性
   * Validate configuration integrity and security
   */
  private validateConfig(): void {
    if (!this.config) {
      throw new Error('Configuration not loaded');
    }

    // 验证必需字段
    if (!this.config.supabaseUrl || !this.config.supabaseAnonKey) {
      throw new Error('Missing required Supabase configuration');
    }

    // 安全检查：防止使用示例或测试值
    const dangerousUrls = [
      'your-project.supabase.co',
      'your-project-id.supabase.co',
      'example.supabase.co',
      'test.supabase.co',
    ];

    const dangerousKeys = [
      'your-anon-key',
      'your_supabase_anon_key_here',
      'your-actual-anon-key-here',
      'example-key',
      'test-key',
    ];

    if (dangerousUrls.some(url => this.config!.supabaseUrl.includes(url))) {
      throw new Error('SECURITY: Cannot use example or compromised Supabase URL');
    }

    if (dangerousKeys.some(key => this.config!.supabaseAnonKey.startsWith(key))) {
      throw new Error('SECURITY: Cannot use example or compromised Supabase key');
    }

    // URL 格式验证
    try {
      new URL(this.config.supabaseUrl);
    } catch {
      throw new Error('Invalid Supabase URL format');
    }
  }

  /**
   * 获取配置
   * Get configuration
   */
  getConfig(): AppConfig {
    if (!this.isInitialized || !this.config) {
      throw new Error('Configuration service not initialized. Call initialize() first.');
    }
    return { ...this.config }; // 返回副本以防止修改
  }

  /**
   * 获取Supabase配置
   * Get Supabase configuration
   */
  getSupabaseConfig(): { url: string; anonKey: string } {
    const config = this.getConfig();
    return {
      url: config.supabaseUrl,
      anonKey: config.supabaseAnonKey,
    };
  }

  /**
   * 检查是否为调试模式
   * Check if debug mode is enabled
   */
  isDebug(): boolean {
    return this.getConfig().isDebugMode;
  }

  /**
   * 获取API基础URL
   * Get API base URL
   * 🔧 [SECURITY REFACTOR] 现在返回空字符串，所有API调用使用相对路径
   */
  getApiBaseUrl(): string {
    return '';
  }

  /**
   * 获取应用信息
   * Get application info
   */
  getAppInfo(): { name: string; version: string } {
    const config = this.getConfig();
    return {
      name: config.appName,
      version: config.appVersion,
    };
  }
}

// 创建全局配置服务实例
export const configService = new ConfigurationService();

// 默认导出配置服务
export default configService;